import { useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { publicApi, setPToken, errMsg } from "@/lib/api";
import { maskPhone, fmtDateTime } from "@/lib/format";
import { Avatar } from "./Avatar";

const Photo = ({ me, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Escolha uma foto."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { await publicApi.post("/public/avatar", fd); toast.success("Foto atualizada."); onDone(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const remove = async () => {
    try { await publicApi.delete("/public/avatar"); toast.success("Foto removida."); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex items-center gap-4">
      <Avatar client={publicApi} path={me.avatar_file_id ? `/public/files/${me.avatar_file_id}` : null} name={me.name} className="h-20 w-20 text-2xl" testId="account-avatar" />
      <div className="flex flex-col gap-1">
        <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="account-photo-input" onChange={pick} />
        <Button data-testid="account-photo-choose" variant="outline" className="h-11 text-[15px]" disabled={busy} onClick={() => ref.current.click()}>
          {busy ? "Enviando…" : me.avatar_file_id ? "Trocar foto" : "Colocar minha foto"}
        </Button>
        {me.avatar_file_id && <button data-testid="account-photo-remove" onClick={remove} className="h-9 text-[15px] font-semibold text-red-700">Remover foto</button>}
      </div>
    </div>
  );
};

const Correction = ({ me, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  if (me.correction_requested_at && !open) {
    return (
      <div data-testid="account-correction-pending" className="flex items-center justify-between gap-2 rounded-md bg-zap-mist p-3 text-[15px]">
        <span>Pedido de correção enviado em {fmtDateTime(me.correction_requested_at)}.</span>
        <button data-testid="account-correction-new" onClick={() => setOpen(true)} className="h-11 shrink-0 px-2 font-semibold text-zap-deep underline">Novo pedido</button>
      </div>
    );
  }
  if (!open) return <Button data-testid="account-correction-request" variant="outline" className="h-12 text-[15px]" onClick={() => setOpen(true)}>Pedir correção dos meus dados</Button>;
  const send = async () => {
    try {
      await publicApi.post("/public/privacy-request", { kind: "correcao", details: text });
      toast.success("Pedido de correção registrado.");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-md border border-zap-line p-3">
      <label htmlFor="correction-text" className="text-[15px] font-medium">O que precisa ser corrigido?</label>
      <Textarea id="correction-text" data-testid="account-correction-text" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} className="text-[16px]" placeholder="Ex.: minha cidade correta é Campinas" />
      <div className="flex gap-2">
        <Button data-testid="account-correction-send" className="h-12 flex-1" disabled={text.trim().length < 3} onClick={send}>Enviar pedido</Button>
        <Button variant="outline" className="h-12 flex-1" onClick={() => setOpen(false)}>Voltar</Button>
      </div>
    </div>
  );
};

export const AccountDialog = ({ open, onClose, me, onChanged }) => {
  const [confirm, setConfirm] = useState(false);
  if (!me) return null;
  const requestDeletion = async () => {
    try {
      await publicApi.post("/public/privacy-request");
      toast.success("Pedido de exclusão registrado.");
      setConfirm(false);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const logout = async () => {
    await publicApi.post("/public/logout").catch(() => {});
    setPToken(null);
    onClose();
    onChanged(true);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="account-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-zap-deep">Meus dados</DialogTitle>
          <DialogDescription className="text-[16px]">Dados usados somente para atendimento da igreja.</DialogDescription>
        </DialogHeader>
        <Photo me={me} onDone={onChanged} />
        <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-[16px]">
          <dt className="text-muted-foreground">Nome</dt><dd data-testid="account-name" className="font-semibold">{me.name}</dd>
          <dt className="text-muted-foreground">WhatsApp</dt><dd>{maskPhone(me.whatsapp)}</dd>
          <dt className="text-muted-foreground">Cidade</dt><dd>{me.city} / {me.state}</dd>
          <dt className="text-muted-foreground">Autorização</dt><dd>{fmtDateTime(me.consent_at)}</dd>
        </dl>
        <Correction me={me} onDone={onChanged} />
        {me.deletion_requested_at ? (
          <p data-testid="account-deletion-pending" className="rounded-md bg-zap-goldsoft p-3 text-[15px]">Pedido de exclusão enviado em {fmtDateTime(me.deletion_requested_at)}.</p>
        ) : confirm ? (
          <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-[15px]">Confirma o pedido de exclusão dos seus dados e mensagens?</p>
            <div className="flex gap-2">
              <Button data-testid="account-deletion-confirm" variant="destructive" className="h-12 flex-1" onClick={requestDeletion}>Sim, pedir exclusão</Button>
              <Button variant="outline" className="h-12 flex-1" onClick={() => setConfirm(false)}>Voltar</Button>
            </div>
          </div>
        ) : (
          <Button data-testid="account-deletion-request" variant="outline" className="h-12 text-[15px]" onClick={() => setConfirm(true)}>Pedir exclusão dos meus dados</Button>
        )}
        <Button data-testid="account-logout" variant="ghost" className="h-12 text-[15px]" onClick={logout}>Sair deste aparelho</Button>
        <a href="/privacidade" target="_blank" rel="noopener noreferrer" data-testid="account-privacy-link" className="text-center text-[15px] font-semibold text-zap-deep underline underline-offset-2">Política de Privacidade</a>
      </DialogContent>
    </Dialog>
  );
};
